import React from 'react';
import { Box, styled } from '@mui/material';
import { HealthGoalCard, type HealthGoalCardProps } from 'components/HealthGoalCard/HealthGoalCard';
import { ProfileHeader, type ProfileHeaderProps } from 'components/ProfileHeader/ProfileHeader';
import { ProfileReminderCard } from 'components/ProfileReminderCard/ProfileReminderCard';
import { ProfileSection } from 'components/ProfileSection/ProfileSection';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';

/** A medication or health reminder as shown on the Profile screen. */
export type ProfileReminder = {
  id: string;
  title: string;
  /** Human-readable schedule, e.g. "Take daily, at 9:00AM". */
  subtitle: string;
};

export type ProfileViewProps = {
  header: ProfileHeaderProps;
  /** Omitted when the member has no health goal; the section stays, without a card. */
  healthGoal?: HealthGoalCardProps;
  medications: ProfileReminder[];
  healthReminders: ProfileReminder[];
};

// Width of the left column's cards on desktop, per design.
const PRIMARY_COLUMN_CONTENT_WIDTH = 343;

// Single column on mobile/tablet; two columns split by a divider from `lg` up.
const ProfileLayout = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(4),
  maxWidth: 1280,
  margin: '0 auto',
  padding: theme.spacing(3, 2),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(5, 7),
  },
  [theme.breakpoints.up('lg')]: {
    flexDirection: 'row',
    gap: 0,
    padding: theme.spacing(6, 9),
  },
}));

const Column = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(4),
  minWidth: 0,
}));

const PrimaryColumn = styled(Column)(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    width: `calc(${PRIMARY_COLUMN_CONTENT_WIDTH}px + ${theme.spacing(8)})`,
    paddingRight: theme.spacing(8),
    borderRight: `1px solid ${theme.palette.grey[400]}`,
  },
}));

const SecondaryColumn = styled(Column)(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flex: 1,
    paddingLeft: theme.spacing(9),
  },
}));

export const ProfileView: React.FC<ProfileViewProps> = ({
  header,
  healthGoal,
  medications,
  healthReminders,
}) => {
  const { translation } = useTranslationContext();

  return (
    <ProfileLayout className="profile-view">
      <PrimaryColumn>
        <ProfileHeader {...header} />

        <ProfileSection title={translation('health_goal')}>
          {healthGoal && <HealthGoalCard {...healthGoal} />}
        </ProfileSection>

        <ProfileSection title={translation('enrolled_care_programs')}>
          {/* TODO: Render enrolled care program cards (+ info button) once the card exists. */}
        </ProfileSection>
      </PrimaryColumn>

      <SecondaryColumn>
        <ProfileSection title={translation('medication_list')}>
          {medications.map(({ id, title, subtitle }) => (
            <ProfileReminderCard key={id} variant="medication" title={title} subtitle={subtitle} />
          ))}
        </ProfileSection>

        <ProfileSection title={translation('health_reminders')}>
          {healthReminders.map(({ id, title, subtitle }) => (
            <ProfileReminderCard key={id} variant="health" title={title} subtitle={subtitle} />
          ))}
        </ProfileSection>
      </SecondaryColumn>
    </ProfileLayout>
  );
};
